import { DEFAULT_GREEN_AUTO_MAP_PARAMS, GreenAutoMapParams } from 'utils/gpsEditor/gpsUtils'
import {
  DEFAULT_GREEN_AUTO_MAP_TUNING,
  GreenAutoMapTuning,
  ParamLevel,
  ProbeThresholdLevel,
  resetActiveGreenAutoMapTuning,
  setActiveGreenAutoMapTuning,
} from 'utils/gpsEditor/parameterSelection'

export const GREEN_AUTO_MAP_DEBUG_PREFERENCES_STORAGE_KEY = 'gps-editor-green-auto-map-debug-preferences'
export const GREEN_AUTO_MAP_TUNING_STORAGE_KEY = 'gps-editor-green-auto-map-tuning'
export const GREEN_AUTO_MAP_PERSISTED_DEBUG_KEYS = [
  'debugStopLogging',
  'debugStepLogging',
  'debugImageLogging',
] as const

export type GreenAutoMapPersistedDebugKey = (typeof GREEN_AUTO_MAP_PERSISTED_DEBUG_KEYS)[number]

export interface DistanceLabelMarker {
  marker: google.maps.marker.AdvancedMarkerElement
  textElement: HTMLSpanElement
}

export const LINE_STROKE_WEIGHT = 4
export const MAX_GPS_POINTS = 36
export const DEFAULT_COURSE_ZOOM = 16
export const DEFAULT_HOLE_ZOOM = 17
export const DEFAULT_ZOOM_WITHOUT_COURSE = 3
export const MAX_MAP_ZOOM = 21
export const DEFAULT_MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID'

const GREEN_MARKER_BASE_SIZE = 21
const TEEBOX_MARKER_BASE_WIDTH = 17
const TEEBOX_MARKER_BASE_HEIGHT = 28
const PREVIEW_LINE_START_OFFSET_PX = 6

const readGreenAutoMapDebugPreferences = (): Pick<GreenAutoMapParams, GreenAutoMapPersistedDebugKey> => {
  const defaults = {
    debugStopLogging: DEFAULT_GREEN_AUTO_MAP_PARAMS.debugStopLogging,
    debugStepLogging: DEFAULT_GREEN_AUTO_MAP_PARAMS.debugStepLogging,
    debugImageLogging: DEFAULT_GREEN_AUTO_MAP_PARAMS.debugImageLogging,
  }

  if (typeof window === 'undefined') {
    return defaults
  }

  try {
    const rawPreferences = window.localStorage.getItem(GREEN_AUTO_MAP_DEBUG_PREFERENCES_STORAGE_KEY)

    if (!rawPreferences) {
      return defaults
    }

    const parsedPreferences = JSON.parse(rawPreferences) as Partial<Record<GreenAutoMapPersistedDebugKey, unknown>>

    return {
      debugStopLogging:
        typeof parsedPreferences.debugStopLogging === 'boolean'
          ? parsedPreferences.debugStopLogging
          : defaults.debugStopLogging,
      debugStepLogging:
        typeof parsedPreferences.debugStepLogging === 'boolean'
          ? parsedPreferences.debugStepLogging
          : defaults.debugStepLogging,
      debugImageLogging:
        typeof parsedPreferences.debugImageLogging === 'boolean'
          ? parsedPreferences.debugImageLogging
          : defaults.debugImageLogging,
    }
  } catch (error) {
    return defaults
  }
}

export const buildGreenAutoMapInitialParams = (): GreenAutoMapParams => ({
  ...DEFAULT_GREEN_AUTO_MAP_PARAMS,
  ...readGreenAutoMapDebugPreferences(),
})

const PARAM_LEVELS: ParamLevel[] = ['strictest', 'tight', 'medium', 'loose', 'ultimate']
const PROBE_THRESHOLD_LEVELS: ProbeThresholdLevel[] = ['strictest', 'tight', 'medium', 'loose']
const L_PARAM_KEYS: (keyof GreenAutoMapTuning['lParams']['medium'])[] = ['deMedStop', 'lAbsStop', 'lStepStop']
const AB_PARAM_KEYS: (keyof GreenAutoMapTuning['abParams']['medium'])[] = [
  'minRatioAb',
  'maxMedShiftAb',
  'abStepStop',
  'abBaseStop',
  'abStepSumStop',
  'abBaseSumStop',
]
const PROBE_THRESHOLD_KEYS: (keyof GreenAutoMapTuning['probeThresholds']['medium'])[] = ['maxAbsAb', 'stdAb', 'stdL']

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

export const parseGreenAutoMapTuning = (raw: unknown): GreenAutoMapTuning => {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Tuning JSON must be an object')
  }
  const obj = raw as Record<string, unknown>
  const lParamsRaw = obj.lParams
  const abParamsRaw = obj.abParams
  const probeThresholdsRaw = obj.probeThresholds
  if (!lParamsRaw || typeof lParamsRaw !== 'object') {
    throw new Error('Missing "lParams" object')
  }
  if (!abParamsRaw || typeof abParamsRaw !== 'object') {
    throw new Error('Missing "abParams" object')
  }
  if (!probeThresholdsRaw || typeof probeThresholdsRaw !== 'object') {
    throw new Error('Missing "probeThresholds" object')
  }

  const readLevelSet = <K extends string>(
    source: Record<string, unknown>,
    level: string,
    keys: readonly K[],
    section: string,
  ): Record<K, number> => {
    const levelRaw = source[level]
    if (!levelRaw || typeof levelRaw !== 'object') {
      throw new Error(`Missing "${section}.${level}"`)
    }
    const levelObj = levelRaw as Record<string, unknown>
    const result = {} as Record<K, number>
    for (const key of keys) {
      const value = levelObj[key]
      if (!isFiniteNumber(value)) {
        throw new Error(`"${section}.${level}.${key}" must be a finite number`)
      }
      result[key] = value
    }
    return result
  }

  const lParams = {} as GreenAutoMapTuning['lParams']
  for (const level of PARAM_LEVELS) {
    lParams[level] = readLevelSet(lParamsRaw as Record<string, unknown>, level, L_PARAM_KEYS, 'lParams')
  }

  const abParams = {} as GreenAutoMapTuning['abParams']
  for (const level of PARAM_LEVELS) {
    abParams[level] = readLevelSet(abParamsRaw as Record<string, unknown>, level, AB_PARAM_KEYS, 'abParams')
  }

  const probeThresholds = {} as GreenAutoMapTuning['probeThresholds']
  for (const level of PROBE_THRESHOLD_LEVELS) {
    probeThresholds[level] = readLevelSet(
      probeThresholdsRaw as Record<string, unknown>,
      level,
      PROBE_THRESHOLD_KEYS,
      'probeThresholds',
    )
  }

  return { lParams, abParams, probeThresholds }
}

export const loadPersistedGreenAutoMapTuning = (): GreenAutoMapTuning | null => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const raw = window.localStorage.getItem(GREEN_AUTO_MAP_TUNING_STORAGE_KEY)
    if (!raw) {
      return null
    }
    return parseGreenAutoMapTuning(JSON.parse(raw))
  } catch (error) {
    console.warn('Failed to load persisted green auto-map tuning, falling back to defaults', error)
    return null
  }
}

export const applyPersistedGreenAutoMapTuning = (): GreenAutoMapTuning => {
  const persisted = loadPersistedGreenAutoMapTuning()
  if (persisted) {
    setActiveGreenAutoMapTuning(persisted)
    return persisted
  }
  resetActiveGreenAutoMapTuning()
  return DEFAULT_GREEN_AUTO_MAP_TUNING
}

export const persistGreenAutoMapTuning = (tuning: GreenAutoMapTuning): void => {
  setActiveGreenAutoMapTuning(tuning)
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(GREEN_AUTO_MAP_TUNING_STORAGE_KEY, JSON.stringify(tuning, null, 2))
  }
}

export const clearPersistedGreenAutoMapTuning = (): void => {
  resetActiveGreenAutoMapTuning()
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(GREEN_AUTO_MAP_TUNING_STORAGE_KEY)
  }
}

applyPersistedGreenAutoMapTuning()

const calculateDistanceMeters = (start: google.maps.LatLngLiteral, end: google.maps.LatLngLiteral): number => {
  const spherical = window.google?.maps?.geometry?.spherical
  if (!spherical) {
    return 0
  }

  return spherical.computeDistanceBetween(
    new window.google.maps.LatLng(start.lat, start.lng),
    new window.google.maps.LatLng(end.lat, end.lng),
  )
}

const getMidpoint = (start: google.maps.LatLngLiteral, end: google.maps.LatLngLiteral): google.maps.LatLngLiteral => ({
  lat: (start.lat + end.lat) / 2,
  lng: (start.lng + end.lng) / 2,
})

const getPathDistanceMeters = (path: google.maps.LatLngLiteral[]): number => {
  const spherical = window.google?.maps?.geometry?.spherical
  if (!spherical || path.length < 2) {
    return 0
  }

  return spherical.computeLength(path)
}

const formatMetersLabel = (meters: number): string => `${Math.round(meters)} m`

export const getScaledGreenMarkerSizePx = (zoom: number): number => Math.max(12, zoom * 1.5)

const getMetersPerPixelAtLatitude = (lat: number, zoom: number): number => {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom
}

export const getOffsetPreviewLineStart = (
  start: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral,
  zoom: number,
): google.maps.LatLngLiteral => {
  const spherical = window.google?.maps?.geometry?.spherical

  if (!spherical) {
    return start
  }

  const startLatLng = new window.google.maps.LatLng(start.lat, start.lng)
  const endLatLng = new window.google.maps.LatLng(end.lat, end.lng)
  const distanceMeters = spherical.computeDistanceBetween(startLatLng, endLatLng)

  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) {
    return start
  }

  const offsetMeters =
    (getScaledGreenMarkerSizePx(zoom) / 2 + PREVIEW_LINE_START_OFFSET_PX) * getMetersPerPixelAtLatitude(start.lat, zoom)

  if (distanceMeters <= offsetMeters) {
    return start
  }

  const heading = spherical.computeHeading(startLatLng, endLatLng)
  const offsetPoint = spherical.computeOffset(startLatLng, offsetMeters, heading)

  return {
    lat: offsetPoint.lat(),
    lng: offsetPoint.lng(),
  }
}

export const getMainMarkerDimensions = (
  locationType: 'teebox' | 'green',
  zoom: number,
): { width: number; height: number } => {
  const greenSizePx = getScaledGreenMarkerSizePx(zoom)

  if (locationType === 'green') {
    return { width: greenSizePx, height: greenSizePx }
  }

  return {
    width: (greenSizePx / GREEN_MARKER_BASE_SIZE) * TEEBOX_MARKER_BASE_WIDTH,
    height: (greenSizePx / GREEN_MARKER_BASE_SIZE) * TEEBOX_MARKER_BASE_HEIGHT,
  }
}

const createDistanceLabelContent = (): { container: HTMLDivElement; textElement: HTMLSpanElement } => {
  const container = document.createElement('div')
  container.className = 'gps-editor-distance-label'

  const textElement = document.createElement('span')
  textElement.textContent = ''
  textElement.className = 'gps-editor-distance-label-text'

  container.appendChild(textElement)

  return { container, textElement }
}

export const setDistanceLabel = (
  labelMarker: DistanceLabelMarker,
  start: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral,
): void => {
  labelMarker.marker.position = getMidpoint(start, end)
  labelMarker.textElement.textContent = formatMetersLabel(calculateDistanceMeters(start, end))
}

export const setPathDistanceLabel = (labelMarker: DistanceLabelMarker, path: google.maps.LatLngLiteral[]): void => {
  if (path.length < 2) {
    return
  }

  labelMarker.marker.position = getMidpoint(path[0], path[path.length - 1])
  labelMarker.textElement.textContent = formatMetersLabel(getPathDistanceMeters(path))
}

export const createDistanceLabelMarker = (map: any): DistanceLabelMarker | null => {
  if (!window.google?.maps?.marker?.AdvancedMarkerElement) {
    return null
  }

  const { container, textElement } = createDistanceLabelContent()
  const marker = new window.google.maps.marker.AdvancedMarkerElement({
    map,
    gmpClickable: false,
    zIndex: 110,
  })

  ;(marker as any).anchorLeft = '-50%'
  ;(marker as any).anchorTop = '-50%'
  marker.append(container)

  return { marker, textElement }
}

const getCoordinateValue = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'string') {
    const parsedValue = parseFloat(value)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }

  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

const getCoordinateKey = (marker?: CourseGpsMarker): string => {
  if (!marker) {
    return 'missing'
  }

  const lat = getCoordinateValue(marker.lat)
  const lng = getCoordinateValue(marker.lng)

  if (lat === null || lng === null) {
    return 'missing'
  }

  return `${lat.toFixed(6)},${lng.toFixed(6)}`
}

export const getHoleLayoutKey = (holeModel: {
  teebox?: CourseGpsMarker
  fairway?: CourseGpsMarker
  green?: CourseGpsMarker
}): string | null => {
  if (!holeModel.teebox || !holeModel.green) {
    return null
  }

  return [
    getCoordinateKey(holeModel.teebox),
    getCoordinateKey(holeModel.fairway),
    getCoordinateKey(holeModel.green),
  ].join('|')
}

export const toLatLngPath = (path: CourseGpsMarker[]): google.maps.LatLngLiteral[] | null => {
  const latLngPath = path.map((marker) => {
    const lat = getCoordinateValue(marker.lat)
    const lng = getCoordinateValue(marker.lng)

    if (lat === null || lng === null) {
      return null
    }

    return { lat, lng }
  })

  if (latLngPath.some((point) => point === null)) {
    return null
  }

  return latLngPath as google.maps.LatLngLiteral[]
}

export const clonePolygonPoints = (points: GpsEditorPolygonPoint[]): GpsEditorPolygonPoint[] => {
  return points.map((point) => ({ ...point }))
}

export const buildCompleteCourseGpsData = (
  markers: CourseGpsMarker[],
  polygons: GpsEditorPolygon[],
): { markers: CourseGpsMarker[]; polygons: GpsEditorPolygon[] } => {
  const playableHoleLayouts = Array.from({ length: 18 }, (_, index) => index + 1)
    .map((hole) => {
      const holeMarkers = markers.filter((marker) => marker.hole === hole)
      const teeboxMarker = holeMarkers.find((marker) => marker.locationType === 'teebox')
      const greenMarker = holeMarkers.find((marker) => marker.locationType === 'green')

      if (!teeboxMarker || !greenMarker) {
        return null
      }

      return {
        hole,
        markers: holeMarkers.filter(
          (marker) =>
            marker.locationType === 'teebox' || marker.locationType === 'green' || marker.locationType === 'fairway',
        ),
        polygon: polygons.find((polygon) => (polygon.hole ?? hole) === hole),
      }
    })
    .filter(
      (holeLayout): holeLayout is { hole: number; markers: CourseGpsMarker[]; polygon: GpsEditorPolygon | undefined } =>
        Boolean(holeLayout),
    )

  if (playableHoleLayouts.length === 0 || playableHoleLayouts.length === 18) {
    return {
      markers,
      polygons,
    }
  }

  const playableHoles = new Set(playableHoleLayouts.map((holeLayout) => holeLayout.hole))
  const missingHoles = Array.from({ length: 18 }, (_, index) => index + 1).filter((hole) => !playableHoles.has(hole))
  const missingHoleSet = new Set(missingHoles)

  const preservedMarkers = markers.filter((marker) => !missingHoleSet.has(marker.hole))
  const preservedPolygons = polygons.filter((polygon) => !missingHoleSet.has(polygon.hole ?? -1))

  const populatedMarkers = missingHoles.flatMap((hole, index) => {
    const sourceLayout = playableHoleLayouts[index % playableHoleLayouts.length]

    return sourceLayout.markers.map((marker) => ({
      ...marker,
      hole,
      isDragging: false,
      isActive: false,
    }))
  })

  const populatedPolygons = missingHoles.flatMap((hole, index) => {
    const sourceLayout = playableHoleLayouts[index % playableHoleLayouts.length]

    if (!sourceLayout.polygon) {
      return []
    }

    return [
      {
        ...sourceLayout.polygon,
        id: `polygon-${hole}-${index}-${crypto.randomUUID()}`,
        hole,
        points: sourceLayout.polygon.points.map((point) => ({ ...point })),
      },
    ]
  })

  return {
    markers: [...preservedMarkers, ...populatedMarkers],
    polygons: [...preservedPolygons, ...populatedPolygons],
  }
}
